import * as queryString from 'query-string';
import * as _ from 'underscore';

import { appStore } from '../../app/AppStore';
import { selectContentCounts, selectContentCountsUpToDate } from '../../app/selectors/ContentCountsSelector';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { ISpaceInfoTypeItemCount } from '../../app/state/ContentCountsState';
import { UrlParamKeys } from '../../app/UrlParamKeys';
import { Location } from '../../common/react/Location';
import { Helpers } from '../helpers/Helpers';
import { getQueryFromUrl } from '../helpers/UrlHelpers';
import { L10n } from '../L10n/L10n';
import { IDataSourceDetailInfo } from '../models/IDataSourceInfo';
import { Module } from '../Module';
import { RefreshOptionType } from '../resources/Datasources';
import { IGetPreferredRefreshOptionResult } from '../ServerApi/requests/GetPreferredRefreshOption';
import { RefreshModeTypes } from '../ServerApi/types/IRemoteRefreshProperties';
import { PublishDatasourceAction } from '../services/PublishDatasourceAction';
import { ToasterService, ToastType } from '../services/ToasterService';

export interface IDatasourcePublishResultScope extends ng.IScope {
  datasource: IDataSourceDetailInfo;
  publishResult: IDatasourcePublishResult;
}

export interface IDatasourcePublishResult extends ISyncPublishResult {
  preferredRefreshOption: IGetPreferredRefreshOptionResult;
}

export interface ISyncPublishResult {
  isSyncAvailable: boolean;
  isSyncRunning: boolean;
  remoteAgentWorkflowId: string;
  publishedWithLQS: boolean;
}

// This Ctrl can go with FeatureFlags.removeDSRoute
export class DatasourcePublishResultCtrl {

  public static $inject = [
    'datasourceDetailInfo',
    'preferredRefreshOption',
    'DatasourcePublishResultService'
  ];

  constructor(
    datasourceDetailInfo: IDataSourceDetailInfo,
    preferredRefreshOption: IGetPreferredRefreshOptionResult,
    datasourcePublishResultService: DatasourcePublishResultService
  ) {
    datasourcePublishResultService.handlePublishedDatasource(datasourceDetailInfo, preferredRefreshOption, '');
  }
}

Module.instance().controller('DatasourcePublishResultCtrl', DatasourcePublishResultCtrl);

export class DatasourcePublishResultService {
  public static $inject = [
    '$translate',
    'PublishDatasourceAction'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private PublishDatasourceAction: PublishDatasourceAction
  ) {}

  public handlePublishedDatasource(datasourceDetailInfo: IDataSourceDetailInfo,
    preferredRefreshOption: IGetPreferredRefreshOptionResult,
    resultUrl: string) {


    const publishResult: IDatasourcePublishResult = {
      preferredRefreshOption: null,
      isSyncAvailable: null,
      isSyncRunning: null,
      remoteAgentWorkflowId: null,
      publishedWithLQS: null
    };

    publishResult.preferredRefreshOption = preferredRefreshOption;

    let search = getQueryFromUrl(resultUrl);

    switch (preferredRefreshOption.suggestion) {
      case RefreshOptionType.OnlineSync:
        // Before we go, pass along the sync-related query string parameters
        if (_.has(search, UrlParamKeys.SyncAvailable)) {
          publishResult.isSyncAvailable = Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.SyncAvailable]);
        }
        if (_.has(search, UrlParamKeys.SyncRunning)) {
          publishResult.isSyncRunning = Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.SyncRunning]);
        }
        if (_.has(search, UrlParamKeys.RemoteAgentWorkflowId)) {
          publishResult.remoteAgentWorkflowId = search[UrlParamKeys.RemoteAgentWorkflowId];
        }

        this.showSuccessDialogAfterRedirect(publishResult, datasourceDetailInfo);
        break;

      case RefreshOptionType.LiveConnection:
        if (_.has(search, UrlParamKeys.PublishedWithLQS)) {
          publishResult.publishedWithLQS = Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.PublishedWithLQS]);
        }

        this.showSuccessDialogAfterRedirect(publishResult, datasourceDetailInfo);

        break;
      case RefreshOptionType.NotSupported:
      case RefreshOptionType.FeatureDisabled:
        this.showSuccessToast(datasourceDetailInfo);
        break;
      case RefreshOptionType.Server:
        this.showSuccessDialogAfterRedirect(publishResult, datasourceDetailInfo);
      default:
        break;
    }

    // don't need to redirect here after publishing because the DatasourceSpaceRouter handles that- tfs944083
  }

  private showSuccessDialogAfterRedirect(publishResult: IDatasourcePublishResult, datasourceDetailInfo: IDataSourceDetailInfo) {
    // If we open the modal now and then initiate a state change, the modal will not open properly.
    // So, we'll wait for the state to finish changing and then open the modal
    if (publishResult.preferredRefreshOption.suggestion === RefreshOptionType.Server || publishResult.preferredRefreshOption.suggestion === RefreshOptionType.OnlineSync) {
        // We need to get the existing scheduled refreshes count for this data source
        // Wait until the content counts are loaded in the redux store.
        const handlePublishDatasourceAfterGettingCounts = () => {
          const appState = appStore.getState();
          const countsUpToDate = selectContentCountsUpToDate(appState);
          const contentCounts: ISpaceInfoTypeItemCount<'datasource'> = selectContentCounts<'datasource'>(appState, 'datasource');
          if (countsUpToDate) {
            if (datasourceDetailInfo.refreshMode !== RefreshModeTypes.Remote) {
              this.PublishDatasourceAction.handlePublishedDatasource(contentCounts['extractRefreshes'], publishResult, datasourceDetailInfo);
            } else {
              this.PublishDatasourceAction.handlePublishedDatasource(contentCounts['remoteRefreshSchedules'], publishResult, datasourceDetailInfo);
            }
          } else {
            setTimeout(handlePublishDatasourceAfterGettingCounts, 250);
          }
        };
        handlePublishDatasourceAfterGettingCounts();
      } else {
        // For live refresh option
        this.PublishDatasourceAction.handlePublishedDatasource(0, publishResult, datasourceDetailInfo);
      }
  }

  private showSuccessToast(datasourceDetailInfo: IDataSourceDetailInfo) {
    ToasterService.instance.pop({
      type: ToastType.Success,
      text: this.$translate.instant('datasourcePublishSuccess', {
        DATASOURCE_NAME: L10n.elideIfLong(datasourceDetailInfo.name)
      })
    });
  }
}

Module.instance().service('DatasourcePublishResultService', DatasourcePublishResultService);
